import React from 'react';
import theme from 'theme';
import { Theme, Button, Section, Text, Box } from '@quarkly/widgets';
import { Helmet } from 'react-helmet';
import { GlobalQuarklyPageStyles } from 'global-page-styles';
import { Override } from '@quarkly/components';

// We only import react-mic on client so we don't break SSR
let rm;

const details = [
  {
    title: 'Testing',
    background:
      '--color-lightD2 url(https://uploads.quarkly.io/5f44d0da669357001e60ed14/images/default-website-illustration-sea-first.svg?v=2020-11-06T16:37:39.391Z) center center/100% no-repeat',
    backgroundHover:
      '--color-lightD2 url(https://uploads.quarkly.io/5f44d0da669357001e60ed14/images/default-website-illustration-sea-first.svg?v=2020-11-06T16:37:39.391Z) center center/110% no-repeat'
  }
];

// Words to show sorted by grade level
// TODO: what's the difference between 1L and 1U
const words = {
  K: ['baby', 'one', 'boat', 'do', 'car'],
  '1L': ['was', 'daddy', 'book', 'good', 'doll'],
  '1U': ['girl', 'apple', 'they', 'story', 'some'],
  2: ['above', 'what', 'any', 'busy', 'night'],
  3: ['done', 'huge', 'ocean', 'station', 'could'],
  4: ['because', 'echo', 'couple', 'eager', 'together'],
  5: ['bought', 'delicious', 'neighbor', 'achieve', 'region'],
  6: ['malicious', 'bureau', 'similar', 'campaign', 'waltz'],
  '7-8': ['prairie', 'gadget', 'facsimile', 'emphasize', 'prescription'],
  '9-12': ['zealous', 'clique', 'atrocious', 'catastrophe', 'liquidate']
};

const DecodePanels = ({ mobileView, wordLevel = 'K', setTranscript }) => {
  const [token, setToken] = React.useState(null);
  const [socket, setSocket] = React.useState(null);
  const [message, setMessage] = React.useState([]);
  const [texts, setTexts] = React.useState({});
  const [wordIdx, setWordIdx] = React.useState(0);

  const fetchToken = async () => {
    const response = await fetch('https://7mf5rtyu7a.us-east-1.awsapprunner.com/');
    const data = await response.json();
    if (data.error) {
      console.log('Error fetching token:', data.error);
      return;
    }

    const { token } = data;
    setToken(token);
  };

  const closeSocket = async () => {
    if (socket) socket.close();
  };

  const openSocket = async () => {
    const socket = await new WebSocket(
      `wss://api.assemblyai.com/v2/realtime/ws?sample_rate=16000&token=${token}`
    );

    // handle incoming messages to display transcription to the DOM
    socket.onmessage = (message) => {
      let msg = [];
      const res = JSON.parse(message.data);
      texts[res.audio_start] = res.text;
      const keys = Object.keys(texts);
      keys.sort((a, b) => a - b);
      for (const key of keys) {
        if (texts[key]) {
          msg.push(texts[key]);
        }
      }
      console.log('texts:', texts);
      console.log('msg:', msg);
      setTranscript(msg);
      setMessage(msg);
      setTexts(texts);
    };

    socket.onerror = (event) => {
      console.error('socket error:', event);
      socket.close();
    };

    socket.onclose = (event) => {
      console.log('socket closed: ', event);
      setSocket(null);
    };

    socket.onopen = () => {
      // once socket is open, begin recording
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          const recorder = new window.RecordRTC(stream, {
            type: 'audio',
            mimeType: 'audio/webm;codecs=pcm', // endpoint requires 16bit PCM audio
            recorderType: window.StereoAudioRecorder,
            timeSlice: 250, // set 250 ms intervals of data that sends to AAI
            desiredSampRate: 16000,
            numberOfAudioChannels: 1, // real-time requires only one channel
            bufferSize: 4096,
            audioBitsPerSecond: 128000,
            ondataavailable: (blob) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64data = reader.result;

                // audio data must be sent as a base64 encoded string
                if (socket && socket.readyState === WebSocket.OPEN) {
                  socket.send(JSON.stringify({ audio_data: base64data.split('base64,')[1] }));
                }
              };
              reader.readAsDataURL(blob);
            }
          });
          recorder.startRecording();
        })
        .catch((err) => {
          console.error(err);
          socket.close();
        });
    };
    setSocket(socket);
  };

  // fetch token
  React.useEffect(() => {
    if (!token) {
      fetchToken();
    }
  }, [token]);

  React.useEffect(() => {
    if (token && !socket && wordIdx < words[wordLevel].length) {
      openSocket();
    }
  }, [token]);

  React.useEffect(() => {
    // use effect to advance to next word in list
    // because relying on button click can advance when blank sounds were received
    // so by measuring actual words received we know when to advance

    // close the socket when all words are picked up
    if (message.length === words[wordLevel].length) {
      closeSocket();
      rm = null;
    }
    // increment the word idx after each new word is received
    if (message.length == wordIdx + 1) setWordIdx(wordIdx + 1);
  }, [message]);

  return (
    <Section
      lg-padding="30px 0 30px 0"
      quarkly-title="Info"
      justify-content="center"
      padding="30px 0 30px 0"
      sm-padding="10px 0 10px 0"
    >
      <Override
        slot="SectionContent"
        width="100%"
        md-margin="0px 16px 0px 16px"
        min-width="auto"
        margin="0px 32px 0px 32px"
      />

      <Box
        min-width="100px"
        min-height="100px"
        display="flex"
        flex-direction="column"
        align-items="center"
      >
        <Text
          color="--dark"
          lg-text-align="center"
          md-margin="0px 0px 16px 0px"
          md-font="--headline3"
          font="--headline2"
          margin="0px 0px 28px 0px"
        >
          {words[wordLevel][wordIdx]}
        </Text>
        {rm ? (
          <>
            <rm.ReactMic
              record={true}
              strokeColor="#000000"
              className="my-class"
              audioBitsPerSecond={128000}
              mimeType="audio/webm;codecs=opus"
              visualSetting="sinewave"
              echoCancellation={true}
              autoGainControl={true}
              noiseSuppression={true}
              channelCount={2}
            />
          </>
        ) : (
          <Button>{'Next'}</Button>
        )}
      </Box>
    </Section>
  );
};

export const About = () => {
  // bool indicating if device should be shown for mobile
  const [mobileView] = React.useState(window.matchMedia('(max-width: 990px)').matches);
  const [detailsIdx, setDetailsIdx] = React.useState(0);
  const [transcript, setTranscript] = React.useState('');

  // import rm module
  React.useEffect(() => {
    if (!rm) {
      rm = require('@cleandersonlobo/react-mic');
    }
  }, []);

  return (
    <Theme theme={theme}>
      <GlobalQuarklyPageStyles pageUrl={'work'} />
      <Helmet>
        <title>Gryfn Dyslexia Test</title>
        <meta
          name={'description'}
          content={
            'Comprehensive dyslexia screening and personalized support for reading and learning success.'
          }
        />
        <meta property={'og:title'} content={'Gryfn Dyslexia Test'} />
        <meta
          property={'og:description'}
          content={
            'Comprehensive dyslexia screening and personalized support for reading and learning success.'
          }
        />
        <meta
          property={'og:image'}
          content={
            'https://uploads.quarkly.io/5f44d0da669357001e60ed14/images/website-example-quarkly.png?v=2020-11-05T19:48:01.806Z'
          }
        />
        <link
          rel={'shortcut icon'}
          href={'https://uploads.quarkly.io/readme/cra/favicon-32x32.ico'}
          type={'image/x-icon'}
        />
      </Helmet>
      <Section
        padding="25px 0 25px 0"
        lg-padding="25px 0 25px 0"
        justify-content="center"
        quarkly-title="Hero"
      >
        <Override
          slot="SectionContent"
          width="100%"
          margin="0px 32px 0px 12px"
          md-margin="0px 16px 0px 16px"
          min-width="auto"
        />
        <Box
          min-height="180px"
          width="100%"
          display="flex"
          align-items="center"
          justify-content="center"
          background="radial-gradient(at center,--color-darkL2 23.3%,rgba(0,0,0,0) 82.4%),linear-gradient(180deg,rgba(155, 108, 252, 0.15) 0%,transparent 100%) 0 0 no-repeat,--color-darkL2"
        >
          <Text
            justify-content="center"
            quarkly-title="Title"
            text-align="center"
            sm-text-align="center"
            md-font="--headline3"
            font="--headline2"
            margin="0px 0px 0px 0px"
            display="flex"
            align-items="center"
            color="--light"
            sm-font="normal 900 42px/1.2 -apple-system, system-ui, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
            lg-font="--headline3"
          >
            {transcript && transcript.length > 0
              ? 'You said: ' + transcript
              : 'Read the text into your microphone'}
          </Text>
        </Box>
      </Section>
      <DecodePanels mobileView={mobileView} setTranscript={setTranscript} />
    </Theme>
  );
};

export default About;
